// apps/client/src/game/interactions/villager.ts (M2-T11) — talking to villagers and visiting their homes (spec §9.13). Outdoors: Talk to
// the nearest visible villager in front of you (their playback pose, within tuning.villagers.talkRange), or Enter a villager's house at
// its plot door (tuning.town.doorRange; only while they are home and awake, otherwise a toast says they are out or asleep). In their
// room: Talk to them where they stand inside the door, or Leave by the door.
import { tuning, villagerBank, type StringKey } from '@whiskerton/content';
import { parseSpace, villagerSpace, type Vec2, type VillagerId, type VillagerPublic } from '@whiskerton/shared';
import { villagerIndoorSpot, villagerRoomDoor } from '@whiskerton/shared/villagers/schedule';
import { useGame } from '../../state/store';
import { playbackOf } from '../systems/villagers';
import type { Candidate, InteractCtx, Interaction } from '../interact';

const DOOR = 'door:', TALK = 'talk:', EXIT = 'vroom:exit';
const centre = (t: { x: number; y: number }): Vec2 => ({ x: t.x + 0.5, y: t.y + 0.5 });
/** Distance to `at` when it is within `range` and in front of you (or right where you stand), else null (as door.ts). */
function reach(ctx: InteractCtx, at: Vec2, range: number): number | null {
  const dx = at.x - ctx.pos.x, dy = at.y - ctx.pos.y, d = Math.hypot(dx, dy);
  if (d > range) return null;
  if (d > 0.6 && (dx * Math.sin(ctx.f) + dy * Math.cos(ctx.f)) / d < 0.2) return null;
  return d;
}
/** Talk; with a conversation open (M2 playtest: A still said 'Talk'), Next while there are lines to step through, then plain A. */
const talk = (id: VillagerId, at: Vec2): Candidate => {
  const open = useGame.getState().villagersTalk;
  const verb: StringKey = !open ? 'hud.verbTalk' : open.index < open.lines.length ? 'hud.verbNext' : 'hud.a';
  return { id: `${TALK}${id}`, at, verb, priority: 80 };
};

/** Candidates inside a villager's room: Talk (they stand just inside the door) or Leave by the door. */
function indoors(ctx: InteractCtx, villager: VillagerId): Candidate | null {
  const v = ctx.store.villagersList.find((x) => x.id === villager);
  const theme = villagerBank.houseThemes.find((t) => t.id === v?.houseTheme) ?? villagerBank.houseThemes[0]!;
  const spot = villagerIndoorSpot(theme.room), door = centre(villagerRoomDoor(theme.room));
  if (v && reach(ctx, spot, tuning.villagers.talkRange) !== null) return talk(v.id, spot);
  return reach(ctx, door, tuning.town.doorRange) !== null ? { id: EXIT, at: door, verb: 'decor.verbLeave' as StringKey, priority: 40 } : null;
}

export const interaction: Interaction = {
  id: 'villager',
  find(ctx) {
    const s = ctx.store;
    const inside = parseSpace(ctx.space);
    if (inside?.kind === 'vroom') return indoors(ctx, inside.villager);
    if (ctx.space !== 'out') return null;
    const pb = playbackOf(ctx.game);
    // Villagers wait beside the museum and shop doors (shared villagers/schedule.ts doorSpots). Standing on one of those doors, A goes
    // through it: nobody beside it takes the button (v1 round 3 playtest: two of them blocked the museum door). Step off to talk.
    const sites = s.map?.sites, onDoor = !!sites && [sites.museumDoor, sites.shopDoor].some((t) => Math.hypot(ctx.pos.x - t.x - 0.5, ctx.pos.y - t.y - 0.5) < 0.6);
    let near: { c: Candidate; d: number } | null = null, door: { c: Candidate; d: number } | null = null;
    for (const v of s.villagersList) {
      const pose = pb?.poseOf(v.id);
      if (pose?.visible && !onDoor) {
        const d = reach(ctx, pose.pos, tuning.villagers.talkRange);
        if (d !== null && (!near || d < near.d)) near = { c: talk(v.id, pose.pos), d };
      }
      const t = s.map?.plots[v.plot]?.door;                               // WorldSlice.map (revision 4)
      if (!t) continue;
      const at = centre(t), d = reach(ctx, at, tuning.town.doorRange);
      if (d !== null && (!door || d < door.d)) door = { c: { id: `${DOOR}${v.id}`, at, verb: 'hud.verbEnter', priority: 20 }, d };
    }
    return near?.c ?? door?.c ?? null;
  },
  run(ctx, c) {
    const s = useGame.getState();
    if (c.id === EXIT) { void ctx.game.cmd.enterSpace('out'); return; }
    if (c.id.startsWith(DOOR)) {
      const id = c.id.slice(DOOR.length) as VillagerId;
      const v = s.villagersList.find((x) => x.id === id), pose = playbackOf(ctx.game)?.poseOf(id);
      if (!v) return;
      if (!pose || pose.homeAwake) void ctx.game.cmd.enterSpace(villagerSpace(id));   // the server has the last word
      else s.toast(pose.visible ? 'villagers.visitAway' : 'villagers.visitAsleep', { name: v.name });
      return;
    }
    const id = c.id.slice(TALK.length) as VillagerId;
    const open = s.villagersTalk;
    if (open) {                                                              // a talk is already open: step it on, and show it again
      if (open.index < open.lines.length) useGame.setState({ villagersTalk: { ...open, index: open.index + 1 } });
      openTalk(open.villager);
      return;
    }
    void s.villagersStartTalk(id).then((ok) => { if (ok) openTalk(id); });
  },
};
function openTalk(villager: VillagerPublic['id']): void {
  const s = useGame.getState();
  if (!s.screens.some((x) => x.id === 'villagerTalk')) s.openScreen('villagerTalk', { villager });
}
